import { useTranslation } from "react-i18next";
import { BaseLogo } from "../../../core/models/entities/BaseLogo";
import { enviroment } from "../../../enviroment";
import { Dot } from "../Dot";
import { StorageUpdatedAt } from "./StorageUpdatedAt";

interface Props {
  logo: BaseLogo;
}

// A logo carries a storage count when the API mapped one (the org-scoped
// StorageCounts row). A partial or legacy payload leaves it undefined/null -
// then there is nothing truthful to show, so the line stays away rather
// than reading "0".
export function hasKnownStorageCount(
  logo: Pick<BaseLogo, "inStorage">
): boolean {
  return typeof logo.inStorage === "number" && Number.isFinite(logo.inStorage);
}

// The "In storage" line of a logo card in the design detail popup / the
// design editor's logo panel (flag designLogoStorageLineEnabled): the same
// dot + count as the logo detail and the same "x min ago" caption
// (StorageUpdatedAt, itself gated by the freshness flags). Rides on the
// inStorage / storageUpdatedAtUtc the design's logo already has - no call of
// its own. Flag off or unknown count: renders nothing.
export function DesignLogoStorageLine({ logo }: Props) {
  if (!enviroment.designLogoStorageLineEnabled) return null;
  if (!hasKnownStorageCount(logo)) return null;
  return <DesignLogoStorageLineInner logo={logo} />;
}

function DesignLogoStorageLineInner({ logo }: Props) {
  const { t } = useTranslation();
  const count = Math.max(0, logo.inStorage);
  return (
    <div className="flex flex-row gap-x-1 items-center">
      <b>{t("designListStyle.inStorage")}</b>
      <span className="flex items-center">
        <Dot className={count > 0 ? "!bg-status-success" : ""} />
        {count}
      </span>
      <StorageUpdatedAt
        storageUpdatedAtUtc={logo.storageUpdatedAtUtc}
        className="ml-1"
      />
    </div>
  );
}
